import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Heart, Briefcase, Users, Lightbulb, MessageCircle, 
  BookOpen, Video, Calendar, ArrowRight, X, ExternalLink, Book
} from 'lucide-react';

const API_URL = window.location.origin;

// Support types with routing configuration
const SUPPORT_TYPES = [
  {
    id: 'career',
    label: 'Career',
    description: 'Job search, career pivots, workplace challenges',
    icon: Briefcase,
    color: 'bg-blue-500',
    community: 'comm-ic-careers',
    communityName: 'Career Opportunities & Job Leads',
    videos: ['career-clarity', 'negotiation-skills'],
    journalPrompt: 'What does career success look like for you right now? What\'s one small step you could take this week?'
  },
  {
    id: 'relationship',
    label: 'Relationship',
    description: 'Boundaries, communication, discernment',
    icon: Users,
    color: 'bg-pink-500',
    community: 'comm-ic-relationships',
    communityName: 'Relationships, Boundaries & Discernment',
    videos: ['healthy-boundaries', 'difficult-conversations'],
    journalPrompt: 'What relationship is weighing on your heart? What boundary might bring you peace?'
  },
  {
    id: 'prayer',
    label: 'Prayer',
    description: 'Spiritual support, faith community',
    icon: Heart,
    color: 'bg-purple-500',
    community: 'comm-ic-prayer',
    communityName: 'Prayer & Encouragement',
    videos: ['faith-in-transition', 'spiritual-grounding'],
    journalPrompt: 'What are you holding in prayer right now? Where do you need God\'s guidance?'
  },
  {
    id: 'decision',
    label: 'Decision Clarity',
    description: 'Big choices, discernment, next steps',
    icon: Lightbulb,
    color: 'bg-amber-500',
    community: 'comm-ic-main',
    communityName: 'Inner Circle Community',
    videos: ['decision-making', 'trusting-intuition'],
    journalPrompt: 'What decision is before you? What feels heavy about it? What feels peaceful?'
  },
  {
    id: 'feedback',
    label: 'Feedback',
    description: 'Resume, content, ideas review',
    icon: MessageCircle,
    color: 'bg-teal-500',
    community: 'comm-ic-content',
    communityName: 'Content Feedback & Visibility',
    videos: ['personal-branding', 'visibility-strategy'],
    journalPrompt: 'What are you creating or building? What kind of feedback would help you most?'
  }
];

const COACHING_PACKAGE = {
  name: '3-Session Coaching Package',
  price: 1500,
  description: 'Three 60-minute sessions with a certified coach',
  includes: [
    'Personalized guidance',
    'Accountability support',
    'Action planning',
    'Between-session support via messaging'
  ]
};

const INeedSupportButton = ({ className = '', variant = 'floating' }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [step, setStep] = useState('select'); // 'select', 'resources'
  const [supportBooks, setSupportBooks] = useState([]);
  const [storefrontUrl, setStorefrontUrl] = useState('');

  // Fetch support books when dialog opens
  useEffect(() => {
    const fetchSupportBooks = async () => {
      if (isOpen) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API_URL}/api/books/support`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setSupportBooks(response.data.books || []);
          setStorefrontUrl(response.data.storefront_url || '');
        } catch (error) {
          console.error('Failed to fetch support books:', error);
        }
      }
    };
    fetchSupportBooks();
  }, [isOpen]);

  const handleSelectType = (type) => {
    setSelectedType(type);
    setStep('resources');
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedType(null);
    setStep('select');
  };

  const handleGoToCommunity = () => {
    navigate(`/communities/${selectedType.community}`);
    handleClose();
  };

  const handleGoToJournal = () => {
    navigate('/journal', { state: { supportPrompt: selectedType.journalPrompt, supportType: selectedType.label } });
    handleClose();
  };

  const handleBookCoaching = () => {
    navigate('/sessions', { state: { fromSupport: true, supportType: selectedType?.label } });
    handleClose();
  };

  // Card variant - embedded in dashboard
  if (variant === 'card') {
    return (
      <>
        <Card className={`card-base border-terracotta/20 bg-gradient-to-r from-terracotta/5 to-transparent ${className}`}>
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-terracotta/20 flex items-center justify-center">
                  <Heart className="h-6 w-6 text-terracotta" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-forest">Need Support?</h3>
                  <p className="text-sm text-gray-600">Get help with career, relationships, prayer, decisions, or feedback</p>
                </div>
              </div>
              <Button 
                onClick={() => setIsOpen(true)}
                className="bg-terracotta hover:bg-terracotta/90 text-white"
                data-testid="i-need-support-btn"
              >
                <Heart className="h-4 w-4 mr-2" /> I Need Support
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Support Dialog */}
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
                {step === 'select' ? (
                  <>
                    <Heart className="h-5 w-5 text-terracotta" />
                    What kind of support do you need?
                  </>
                ) : (
                  <>
                    <selectedType.icon className={`h-5 w-5 text-white p-1 rounded ${selectedType.color}`} />
                    {selectedType.label} Support
                  </>
                )}
              </DialogTitle>
            </DialogHeader>

            {step === 'select' ? (
              <div className="grid gap-3 mt-4">
                {SUPPORT_TYPES.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => handleSelectType(type)}
                    className="flex items-center gap-4 p-4 rounded-xl border border-sand hover:border-forest/30 hover:bg-sand/30 transition-all text-left group"
                    data-testid={`support-type-${type.id}`}
                  >
                    <div className={`p-3 rounded-xl ${type.color} text-white`}>
                      <type.icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-forest">{type.label}</p>
                      <p className="text-sm text-gray-500">{type.description}</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-gray-300 group-hover:text-forest transition-colors" />
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-4 mt-4">
                <button 
                  onClick={() => setStep('select')}
                  className="text-sm text-gray-500 hover:text-forest flex items-center gap-1"
                >
                  ← Back to support types
                </button>

                <Card className="border-forest/20 bg-forest/5">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <Users className="h-5 w-5 text-forest" />
                      <h3 className="font-medium text-forest">Connect with Community</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      Join <strong>{selectedType.communityName}</strong> to share and receive support.
                    </p>
                    <Button onClick={handleGoToCommunity} className="w-full bg-forest hover:bg-forest/90">
                      Go to Community <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border-terracotta/20 bg-terracotta/5">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <BookOpen className="h-5 w-5 text-terracotta" />
                      <h3 className="font-medium text-forest">Reflect in Your Journal</h3>
                    </div>
                    <p className="text-sm text-gray-600 italic mb-3">
                      &ldquo;{selectedType.journalPrompt}&rdquo;
                    </p>
                    <Button onClick={handleGoToJournal} variant="outline" className="w-full border-terracotta text-terracotta hover:bg-terracotta/10">
                      Start Journaling <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>

                {/* Recommended Books */}
                {supportBooks.length > 0 && (
                  <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <Book className="h-5 w-5 text-blue-600" />
                        <h3 className="font-medium text-forest">Recommended Reading</h3>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Crystal &amp; Dr. Nicole&apos;s book picks to support your journey:
                      </p>
                      <div className="space-y-2 mb-3">
                        {supportBooks.slice(0, 3).map((book, index) => (
                          <a
                            key={index}
                            href={book.amazon_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block p-3 bg-white rounded-lg border border-blue-100 hover:border-blue-300 hover:shadow-sm transition-all group"
                            data-testid={`support-book-${index}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-forest text-sm group-hover:text-blue-700 transition-colors">
                                  {book.title}
                                </p>
                                <p className="text-xs text-gray-500">{book.author}</p>
                                <p className="text-xs text-gray-600 mt-1 line-clamp-2">{book.description}</p>
                              </div>
                              <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-blue-600 flex-shrink-0 mt-1" />
                            </div>
                          </a>
                        ))}
                      </div>
                      {storefrontUrl && (
                        <a
                          href={storefrontUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          Browse full book collection <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <Video className="h-5 w-5 text-purple-600" />
                      <h3 className="font-medium text-forest">Suggested Resources</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      Explore videos in the library that can help with {selectedType.label.toLowerCase()}.
                    </p>
                    <Button 
                      onClick={() => { navigate('/videos'); handleClose(); }} 
                      variant="outline" 
                      className="w-full"
                    >
                      Browse Library <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <Calendar className="h-5 w-5 text-amber-600" />
                      <h3 className="font-medium text-forest">Work with a Coach</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      Get personalized 1:1 guidance with our {COACHING_PACKAGE.name}.
                    </p>
                    <p className="text-lg font-bold text-forest mb-3">
                      ${COACHING_PACKAGE.price.toLocaleString()}
                    </p>
                    <Button onClick={handleBookCoaching} className="w-full bg-amber-600 hover:bg-amber-700">
                      Learn More <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Floating button variant (original)
  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-40 flex items-center gap-2 px-5 py-3 bg-terracotta hover:bg-terracotta/90 text-white rounded-full shadow-lg transition-all hover:scale-105 ${className}`}
        data-testid="i-need-support-btn"
      >
        <Heart className="h-5 w-5" />
        <span className="font-medium">I Need Support</span>
      </button>

      {/* Support Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
              {step === 'select' ? (
                <>
                  <Heart className="h-5 w-5 text-terracotta" />
                  What kind of support do you need?
                </>
              ) : (
                <>
                  <selectedType.icon className={`h-5 w-5 text-white p-1 rounded ${selectedType.color}`} />
                  {selectedType.label} Support
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {step === 'select' ? (
            /* Step 1: Select Support Type */
            <div className="grid gap-3 mt-4">
              {SUPPORT_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => handleSelectType(type)}
                  className="flex items-center gap-4 p-4 rounded-xl border border-sand hover:border-forest/30 hover:bg-sand/30 transition-all text-left group"
                  data-testid={`support-type-${type.id}`}
                >
                  <div className={`p-3 rounded-xl ${type.color} text-white`}>
                    <type.icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-forest">{type.label}</p>
                    <p className="text-sm text-gray-500">{type.description}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-300 group-hover:text-forest transition-colors" />
                </button>
              ))}
            </div>
          ) : (
            /* Step 2: Show Resources */
            <div className="space-y-4 mt-4">
              {/* Back button */}
              <button 
                onClick={() => setStep('select')}
                className="text-sm text-gray-500 hover:text-forest flex items-center gap-1"
              >
                ← Back to support types
              </button>

              {/* Community Card */}
              <Card className="border-forest/20 bg-forest/5">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Users className="h-5 w-5 text-forest" />
                    <h3 className="font-medium text-forest">Connect with Community</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Join <strong>{selectedType.communityName}</strong> to share and receive support from women who understand.
                  </p>
                  <Button onClick={handleGoToCommunity} className="w-full bg-forest hover:bg-forest/90">
                    Go to Community <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>

              {/* Journal Card */}
              <Card className="border-terracotta/20 bg-terracotta/5">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <BookOpen className="h-5 w-5 text-terracotta" />
                    <h3 className="font-medium text-forest">Reflect in Your Journal</h3>
                  </div>
                  <p className="text-sm text-gray-600 italic mb-3">
                    &ldquo;{selectedType.journalPrompt}&rdquo;
                  </p>
                  <Button onClick={handleGoToJournal} variant="outline" className="w-full border-terracotta text-terracotta hover:bg-terracotta/10">
                    Start Journaling <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>

              {/* Recommended Books */}
              {supportBooks.length > 0 && (
                <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <Book className="h-5 w-5 text-blue-600" />
                      <h3 className="font-medium text-forest">Recommended Reading</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      Crystal &amp; Dr. Nicole&apos;s book picks to support your journey:
                    </p>
                    <div className="space-y-2 mb-3">
                      {supportBooks.slice(0, 3).map((book, index) => (
                        <a
                          key={index}
                          href={book.amazon_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block p-3 bg-white rounded-lg border border-blue-100 hover:border-blue-300 hover:shadow-sm transition-all group"
                          data-testid={`support-book-floating-${index}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-forest text-sm group-hover:text-blue-700 transition-colors">
                                {book.title}
                              </p>
                              <p className="text-xs text-gray-500">{book.author}</p>
                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">{book.description}</p>
                            </div>
                            <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-blue-600 flex-shrink-0 mt-1" />
                          </div>
                        </a>
                      ))}
                    </div>
                    {storefrontUrl && (
                      <a
                        href={storefrontUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        Browse full book collection <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Video Resources */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Video className="h-5 w-5 text-purple-600" />
                    <h3 className="font-medium text-forest">Suggested Resources</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Explore videos in the library that can help with {selectedType.label.toLowerCase()}.
                  </p>
                  <Button 
                    onClick={() => { navigate('/videos'); handleClose(); }} 
                    variant="outline" 
                    className="w-full"
                  >
                    Browse Library <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>

              {/* Coaching Card */}
              <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Calendar className="h-5 w-5 text-amber-600" />
                    <h3 className="font-medium text-forest">Work with a Coach</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Get personalized 1:1 guidance with our {COACHING_PACKAGE.name}.
                  </p>
                  <p className="text-lg font-bold text-forest mb-3">
                    ${COACHING_PACKAGE.price.toLocaleString()}
                  </p>
                  <ul className="text-xs text-gray-500 mb-3 space-y-1">
                    {COACHING_PACKAGE.includes.map((item, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-amber-500 rounded-full" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <Button onClick={handleBookCoaching} className="w-full bg-amber-600 hover:bg-amber-700">
                    Learn More <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default INeedSupportButton;
